const DURATION_PARAMS = ['X-Goog-Expires', 'X-Amz-Expires'] as const
const AZURE_EXPIRY_PARAM = 'se'
const AZURE_SIGNATURE_PARAM = 'sig'
const ISO_8601_DATE = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2})?/
const MIN_TRUSTED_TTL_MS = 60 * 1000

const RELATIVE_URL_BASE = 'https://forecast.invalid'

function queryParams(url: string): URLSearchParams | undefined {
  try {
    return new URL(url, RELATIVE_URL_BASE).searchParams
  } catch {
    return undefined
  }
}

function param(params: URLSearchParams, name: string): string | null {
  const wanted = name.toLowerCase()
  for (const [key, value] of params) {
    if (key.toLowerCase() === wanted) return value
  }
  return null
}

/**
 * When a signed storage URL stops working, in epoch ms, or undefined when the
 * URL carries no recognizable expiry. Duration-style signatures (GCS V4, S3
 * SigV4) are measured from `receivedAt`, so a skewed client clock cannot make
 * a freshly signed URL look expired. An Azure SAS carries an absolute `se`,
 * which only counts next to its `sig`. Parameter names match case-insensitively.
 */
export function signedUrlExpiresAt(
  url: string,
  receivedAt: number
): number | undefined {
  const params = queryParams(url)
  if (params === undefined) return undefined

  for (const name of DURATION_PARAMS) {
    const seconds = param(params, name)
    if (seconds === null) continue
    const ms = Number(seconds) * 1000
    if (!Number.isFinite(ms) || ms <= 0) continue
    return receivedAt + ms
  }

  const absolute = param(params, AZURE_EXPIRY_PARAM)
  if (
    absolute === null ||
    param(params, AZURE_SIGNATURE_PARAM) === null ||
    !ISO_8601_DATE.test(absolute)
  ) {
    return undefined
  }
  const ms = Date.parse(absolute)
  return Number.isNaN(ms) ? undefined : ms
}

export type ReceivedSignedUrl = {
  url: string | undefined
  receivedAt: number
}

/**
 * Earliest expiry across a batch, or undefined when no URL carries one. A URL
 * that reads as already dead on arrival (an absolute expiry against a skewed
 * clock) is treated as having no known expiry rather than re-signed forever.
 */
export function earliestExpiresAt(
  received: ReadonlyArray<ReceivedSignedUrl>
): number | undefined {
  let earliest: number | undefined
  for (const { url, receivedAt } of received) {
    if (typeof url !== 'string' || url === '') continue
    const expiresAt = signedUrlExpiresAt(url, receivedAt)
    if (
      expiresAt === undefined ||
      expiresAt - receivedAt < MIN_TRUSTED_TTL_MS
    ) {
      continue
    }
    if (earliest === undefined || expiresAt < earliest) earliest = expiresAt
  }
  return earliest
}

/** True when `expiresAt` is known and is within `margin` ms of `now` or past it. */
export function isExpired(
  expiresAt: number | undefined,
  now: number,
  margin = 0
): boolean {
  return expiresAt !== undefined && now >= expiresAt - margin
}
